import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { resolveApiErrorMessage } from '@/api/resolveApiErrorMessage';
import type { Role } from '@/api/access/accessApi.types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ModalOverlay } from '@/components/ui/modalOverlay';
import { SearchableSelect, type SearchableSelectOption } from '@/components/ui/searchableSelect';
import { FieldError } from '@/components/ui/fieldError';
import { Icon } from '@/components/common/Icon';
import { SEARCH_DEBOUNCE_MILLISECONDS } from '@/constants/adminPagination';
import { PLATFORM_SCOPE_CODE } from '@/constants/scopes';
import { useCreateInvitationMutation } from '@/hooks/useCreateInvitationMutation';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { useRolesQuery } from '@/hooks/useRolesQuery';
import { isBlank, isValidEmail } from '@/utils/formValidation';
import { showErrorNotification, showSuccessNotification } from '@/utils/notifications';

interface InviteUserModalProps {
  onClose: () => void;
}

function buildRoleOptions(
  loadedRoles: Role[],
  selectedRoleOption: SearchableSelectOption | null
): SearchableSelectOption[] {
  const loadedOptions = loadedRoles.map(function toOption(role) {
    return { value: role.id, label: role.name };
  });
  if (selectedRoleOption === null) {
    return loadedOptions;
  }
  const selectedRoleIsListed = loadedOptions.some(function matchesSelectedRole(option) {
    return option.value === selectedRoleOption.value;
  });
  return selectedRoleIsListed ? loadedOptions : [selectedRoleOption, ...loadedOptions];
}

export function InviteUserModal({ onClose }: InviteUserModalProps) {
  const { t } = useTranslation(['admin', 'common']);
  const [roleSearchInput, setRoleSearchInput] = useState('');
  const debouncedRoleSearch = useDebouncedValue(roleSearchInput, SEARCH_DEBOUNCE_MILLISECONDS);
  const rolesQuery = useRolesQuery(1, debouncedRoleSearch, PLATFORM_SCOPE_CODE);
  const [selectedRoleOption, setSelectedRoleOption] = useState<SearchableSelectOption | null>(null);
  const createInvitationMutation = useCreateInvitationMutation();

  const [emailAddress, setEmailAddress] = useState('');
  const [fullName, setFullName] = useState('');
  const [selectedRoleId, setSelectedRoleId] = useState('');
  const [showErrors, setShowErrors] = useState(false);

  const emailError =
    showErrors === true && isValidEmail(emailAddress) === false ? 'validation:email' : null;
  const roleError =
    showErrors === true && isBlank(selectedRoleId) ? 'validation:requiredField' : null;

  const roleOptions = buildRoleOptions(
    rolesQuery.data === undefined ? [] : rolesQuery.data.items,
    selectedRoleOption
  );

  function submitInvitation(formEvent: FormEvent<HTMLFormElement>) {
    formEvent.preventDefault();
    if (isValidEmail(emailAddress) === false || isBlank(selectedRoleId)) {
      setShowErrors(true);
      return;
    }
    createInvitationMutation.mutate(
      {
        email: emailAddress.trim(),
        fullName: fullName.trim().length === 0 ? null : fullName.trim(),
        roleId: selectedRoleId,
        scopeCode: PLATFORM_SCOPE_CODE,
      },
      {
        onSuccess: function handleInvitationSent() {
          showSuccessNotification(t('admin:users.inviteSuccess'));
          onClose();
        },
        onError: function handleInvitationError(invitationError) {
          const backendMessage = resolveApiErrorMessage(invitationError);
          showErrorNotification(
            backendMessage.length > 0 ? backendMessage : t('admin:errors.generic')
          );
        },
      }
    );
  }

  return (
    <ModalOverlay onRequestClose={onClose}>
      <form
        onSubmit={submitInvitation}
        className="w-full max-w-lg max-h-[90vh] flex flex-col bg-zoowhite border border-zooborder rounded-3xl shadow-2xl overflow-hidden"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-zooborder">
          <h2 className="font-bold text-zooindigo">{t('admin:users.inviteTitle')}</h2>
          <Button
            variant="ghost"
            size="icon"
            aria-label={t('common:actions.close')}
            onClick={onClose}
          >
            <Icon name="X" className="text-zoomuted" />
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          <p className="text-sm text-zoomuted">{t('admin:users.inviteDescription')}</p>
          <div className="space-y-1.5">
            <Label htmlFor="invite-email">{t('admin:users.emailLabel')}</Label>
            <Input
              id="invite-email"
              type="email"
              value={emailAddress}
              hasError={emailError !== null}
              placeholder={t('admin:users.emailPlaceholder')}
              onChange={function updateEmail(changeEvent) {
                setEmailAddress(changeEvent.target.value.trim());
              }}
            />
            <FieldError messageKey={emailError} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="invite-name">{t('admin:users.fullNameLabel')}</Label>
            <Input
              id="invite-name"
              type="text"
              value={fullName}
              placeholder={t('admin:users.fullNamePlaceholder')}
              onChange={function updateName(changeEvent) {
                setFullName(changeEvent.target.value);
              }}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="invite-role">{t('admin:users.roleLabel')}</Label>
            <SearchableSelect
              inputId="invite-role"
              value={selectedRoleId}
              hasError={roleError !== null}
              placeholder={t('admin:users.rolePlaceholder')}
              options={roleOptions}
              isLoading={rolesQuery.isFetching}
              disableBuiltInFilter
              onInputChange={setRoleSearchInput}
              onChange={function updateRole(nextValue) {
                const chosenRoleOption = roleOptions.find(function matches(option) {
                  return option.value === nextValue;
                });
                setSelectedRoleOption(chosenRoleOption === undefined ? null : chosenRoleOption);
                setSelectedRoleId(nextValue);
              }}
            />
            <FieldError messageKey={roleError} />
          </div>
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t border-zooborder bg-zoooffwhite">
          <Button type="button" variant="outline" onClick={onClose}>
            {t('common:actions.cancel')}
          </Button>
          <Button type="submit" isLoading={createInvitationMutation.isPending}>
            {t('admin:users.sendInvite')}
          </Button>
        </div>
      </form>
    </ModalOverlay>
  );
}
